// No ColorDefinitions needed
import { logger } from '@adornis/base/logging.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { TextMultipleLangItem } from '../entities/TextMultipleLangItem.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { WorkshopSet } from '../entities/WorkshopSet.js';
import { ParticipationState } from '../enums/enums.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { fileAttachment } from '../mails/file-attachment.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';
import { getWorkshopSetByID } from '../api/workshop-set.js';
import { getInstitutionByID } from '../api/institution.js';
import { getUserByID } from '../api/users.js';

// Using the existing NUTZER_TEILNAHME template since it already handles certificates
defineMailTemplate(MailTemplateType.NUTZER_WORKSHOP_SET_TEILNAHME, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Bestätigung der Teilnahme am Workshop-Set mit Zertifikat',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>wir freuen uns, Ihnen mitteilen zu können, dass Sie erfolgreich am Workshop-Set "{{product-name}}" teilgenommen haben.</p>
<p>Als Nachweis Ihrer Teilnahme finden Sie im Anhang Ihr Zertifikat.</p>
<p>Wir hoffen, die Workshops haben Ihnen gefallen und waren hilfreich für Sie. Bei Fragen können Sie sich jederzeit an uns wenden.</p>
<p>Mit freundlichen Grüßen</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>We are pleased to inform you that you have successfully completed the workshop set "{{product-name}}".</p>
<p>As proof of your participation, please find your certificate attached.</p>
<p>We hope you enjoyed the workshops and found them useful. If you have any questions, please do not hesitate to contact us.</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.NUTZER_WORKSHOP_SET_TEILNAHME),
  attachments: [],
  displayName: 'Workshop-Set Teilnahmebestätigung mit Zertifikat',
  editorHint: () => institutionMailHint(MailTemplateType.NUTZER_WORKSHOP_SET_TEILNAHME),
});

/**
 * Notifies a user about their successful completion of a workshop set with a certificate attachment
 * @param workshopSetID The ID of the workshop set that was completed
 * @param userID The ID of the user who completed the workshop set
 */
export async function notifyWorkshopSetCompletion(workshopSetID: string, userID: string, certificateID: string) {
  // Get the workshop set
  const workshopSet = await getWorkshopSetByID(workshopSetID)({
    ...selectionSet(() => WorkshopSet, 1),
    institutionID: 1,
    name: 1,
    translatedDescriptionHTML: selectionSet(() => TextMultipleLangItem, 2),
  });

  if (!workshopSet) throw new Error(`Could not find workshop set with ID ${workshopSetID}`);

  // Get the institution
  const institution = await getInstitutionByID(workshopSet.institutionID)({ ...selectionSet(() => Institution, 1) });

  if (!institution) throw new Error(`Could not find institution with ID ${workshopSet.institutionID}`);

  // Get the user
  const user = await getUserByID(userID)({
    _id: 1,
    email: 1,
    title: 1,
    username: 1,
    firstName: 1,
    lastName: 1,
    dateOfBirth: 1,
    whatsappNotification: 1,
    suspentions: { _id: 1, institutionID: 1 },
    whatsappPhone: 1,
  });

  if (!user) throw new Error(`Could not find user with ID ${userID}`);

  // Get the workshops in this workshop set
  const workshops = await loadMongoData(Workshop, {
    institutionID: workshopSet.institutionID,
    'affiliation.affiliation': WorkshopSet._class,
    'affiliation.affiliationID': workshopSetID,
  });

  if (!workshops || workshops.length === 0)
    throw new Error(`No workshops found for workshop set with ID ${workshopSetID}`);

  // Check if user has participated in all workshops in the set
  const workshopParticipations = await Promise.all(
    workshops.map(async workshop => {
      const participations = await loadMongoData(Participation, {
        productID: workshop._id,
        userID: user._id,
        state: ParticipationState.participated,
      });
      return participations?.[0] || null;
    }),
  );

  const allWorkshopsParticipated = workshopParticipations.every(p => p !== null);
  if (!allWorkshopsParticipated) {
    throw new Error(`User has not participated in all workshops in the workshop set`);
  }

  // Get the mail template
  const mailTemplate = (await getMailTemplateForInstitution(
    workshopSet.institutionID,
    MailTemplateType.NUTZER_WORKSHOP_SET_TEILNAHME,
  )(InstitutionMailTemplate.allFields)) as InstitutionMailTemplate;

  // Generate a login token
  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  // Prepare mail variables
  const mailVars = {
    [MailTemplateVars.PRODUCT_NAME]: workshopSet.name,
    [MailTemplateVars.TITLE]: user.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
  };

  const { subject, content } = (await assembleInstitutionMail({
    template: mailTemplate,
    infos: mailVars as any,
  })) as { subject: string; content: any };

  if (!content) throw new Error('Mail template has no content!');
  if (!subject) throw new Error('Mail template has no subject!');

  const sendEmail = async () => {
    await sendInstitutionEmail({
      email: user.email,
      subject,
      content,
      institutionID: institution._id,
      attachments: [await fileAttachment(certificateID, 'Zertifikat.pdf')],
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, institution._id);
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID: institution._id,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.NUTZER_WORKSHOP_SET_TEILNAHME,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        workshopSet.name || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }

  return { success: true };
}
